import { Badge } from "../../components/ui/badge";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Progress } from "../../components/ui/progress";
import { 
  CheckCircle2, 
  RefreshCcw, 
  Clock, 
  CheckIcon, 
  Loader2Icon,
  BrainCircuit,
  FileText,
  Image,
  Search,
  Sparkles,
  Zap,
  Lightbulb
} from "lucide-react";
import type { EnrichmentJob } from "wasp/entities";
import { formatDate } from "../../admin/dashboards/enrichmentViewer/utils";
import { useEffect, useState } from "react";
import { setJobFilter } from "../../store/filtersSlice";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store/store";
import { analytics } from "../../analytics";
import { updateEnrichmentTask, useQuery } from "wasp/client/operations";
import { getEnrichmentTask } from "wasp/client/operations";
import { useTranslation } from "../../i18n/hooks";
import { useJobProgress } from "../../hooks/useJobProgress";

interface EnrichmentStep {
  id: number;
  label: string;
  description: string;
  improvements: string;
  icon: React.FC<any>;
  expectedDuration: number;
}

const CircularLoader = () => (
  <div className="w-5 h-5 relative">
    <div className="w-full h-full rounded-full border-2 border-blue-200 border-t-blue-600 animate-spin" />
  </div>
);

const getStatusIcon = (status: string) => {
  switch (status) {
    case "completed":
      return <CheckCircle2 className="w-5 h-5 text-green-500" />;
    case "in_progress":
      return <RefreshCcw className="w-5 h-5 text-blue-500 animate-spin" />;
    case "queued":
      return <Clock className="w-5 h-5 text-gray-500" />;
    default:
      return null;
  }
};

const getStatusColor = (status: string) => {
  switch (status) {
    case "completed":
      return "bg-green-100 text-green-800";
    case "in_progress":
      return "bg-blue-100 text-blue-800";
    case "queued":
      return "bg-gray-100 text-gray-800";
    default:
      return "";
  }
};

const JobInProgressCard = ({ job }: { job: EnrichmentJob }) => {
  const dispatch = useDispatch();
  const jobFilter = useSelector((state: RootState) => state.filters.jobFilter);
  const {currentUser} = useSelector((state: RootState) => state.user);
  const { t } = useTranslation();
  
  // useQuery kullanarak iş durumunu sorgula
  const { data: updatedJob, isLoading, error: queryError } = useQuery(
    getEnrichmentTask, 
    { jobId: job.id }, 
    { 
      // İş tamamlandığında veya başarısız olduğunda sorgu yapmayı durdur
      enabled: !!job.id && job.status !== "completed" && job.status !== "failed", 
      refetchInterval: 3000, 
      refetchOnWindowFocus: true, 
      refetchIntervalInBackground: true 
    }
  );
  
  // En güncel iş verisini kullan
  const currentJob = updatedJob || job;

  const enrichmentSteps: EnrichmentStep[] = [
    {
      id: 1,
      label: t('jobsView.analyzing_content'),
      description: t('jobsView.evaluating_current_content'),
      improvements: t('jobsView.identifying_improvement_areas'),
      icon: BrainCircuit,
      expectedDuration: 20
    },
    {
      id: 2,
      label: t('jobsView.scanning_visuals'),
      description: t('jobsView.extracting_product_information'),
      improvements: t('jobsView.processing_image_attributes'),
      icon: Image,
      expectedDuration: 25
    },
    {
      id: 3,
      label: t('jobsView.improving_copy'),
      description: t('jobsView.enhancing_product_descriptions'),
      improvements: t('jobsView.adding_usps_features'),
      icon: FileText,
      expectedDuration: 30
    },
    {
      id: 4,
      label: t('jobsView.seo_optimization'),
      description: t('jobsView.maximizing_search_visibility'),
      improvements: t('jobsView.optimizing_keywords_meta'),
      icon: Search,
      expectedDuration: 25
    }
  ];

  const { progress: lastProgress, message: lastMessage } = useJobProgress({
    jobId: job.enrichmentJobId!,
    initialProgress: job.progress ?? 0,
    onCompleted: async () => {
      if (!job.enrichmentJobId) {
        alert("Enrichment Job Id is missing");
        return;
      }
      await updateEnrichmentTask({
        id: job.id,
        enrichmentJobId: job.enrichmentJobId,
        progress: 100,
        isCompleted: true,
      });
      analytics.sendEnhancementJobCompletedEvent({
        jobId: job.enrichmentJobId,
        email: currentUser?.email ?? "unknown",
      })
      if(jobFilter === "running"){
        dispatch(setJobFilter("export-ready"));
      }
    },
    async onError(error: string) {
      await updateEnrichmentTask({
        id: job.id,
        enrichmentJobId: job.enrichmentJobId!,
        progress: 0,
        isCompleted: false,
        isFailed: true,
        failReason: error,
      });
    },
  });

  // Backend artık iş ilerlemesini yönetiyor (checkExportJobStatus cron job)
  const progress = lastProgress > (currentJob.progress || 0) ? lastProgress : currentJob.progress || 0;
  const message = lastMessage || currentJob.failReason || t('jobsView.content_enhancement_progress');
  
  // İş tamamlandığında analitik olay gönder
  useEffect(() => {
    if (currentJob.status === "completed") {
      analytics.sendEnhancementJobCompletedEvent({
        jobId: currentJob.enrichmentJobId || "",
        email: currentUser?.email ?? "unknown",
      });
      
      // İş tamamlandığında hemen export-ready filter'a geç
      dispatch(setJobFilter("export-ready"));
    }
  }, [currentJob.status, currentJob.enrichmentJobId, currentUser?.email, dispatch]);

  // Eğer iş tamamlandıysa, JobCompletedCard'ı göster
  if (currentJob.status === "completed") {
    return null; // JobInProgressCard'ı gizle
  }

  const getStepStatus = (stepIndex: number) => {
    const stepThreshold = (stepIndex + 1) * (100 / enrichmentSteps.length);
    const isActive = progress >= (stepThreshold - 25) && progress < stepThreshold;
    const isComplete = progress >= stepThreshold;
    return { isActive, isComplete };
  };

  return (
    <Card className="w-full bg-white shadow-md hover:shadow-lg transition-all duration-300">
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle className="flex items-center space-x-2">
            <span className="bg-blue-100 p-2 rounded-lg">
              <Zap className="w-5 h-5 text-blue-600" />
            </span>
            <span>{t('jobsView.content_enhancement_progress')}</span>
          </CardTitle>
          <div className="flex items-center space-x-2">
            <Lightbulb className="w-4 h-4 text-amber-500" />
            <span className="text-sm text-slate-600">
              {Math.round(progress)}% {t('jobsView.completed')}
            </span>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Main Progress Bar */}
        <div className="w-full">
          <Progress 
            value={progress} 
            className="h-2 bg-slate-100" 
          />
          <p className="text-sm text-slate-500 mt-2">{message}</p>
        </div>

        {/* Steps Progress */}
        <div className="space-y-3">
          {enrichmentSteps.map((step, index) => {
            const { isActive, isComplete } = getStepStatus(index);
            const Icon = step.icon;
            
            return (
              <div
                key={step.id}
                className={`p-3 rounded-lg border transition-all duration-300 ${
                  isActive ? 'bg-blue-50 border-blue-200 shadow-sm scale-[1.01]' :
                  isComplete ? 'bg-green-50 border-green-200' :
                  'bg-slate-50 border-slate-200'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors duration-300 ${
                    isComplete ? 'bg-green-100' :
                    isActive ? 'bg-blue-100' :
                    'bg-slate-100'
                  }`}>
                    {isComplete ? (
                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                    ) : isActive ? (
                      <CircularLoader />
                    ) : (
                      <Icon className="w-4 h-4 text-slate-400" />
                    )}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-center">
                      <h3 className={`font-medium ${
                        isComplete ? 'text-green-700' :
                        isActive ? 'text-blue-700' :
                        'text-slate-700'
                      }`}>
                        {step.label}
                      </h3>
                      {(isActive || isComplete) && (
                        <span className={`text-xs font-medium px-2 py-1 rounded-full bg-opacity-10 truncate ml-2 ${
                          isComplete ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                        }`}>
                          {step.improvements}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {step.description}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Real-time Metrics */}
        {progress > 20 && (
          <div className="grid grid-cols-3 gap-4 pt-2">
            <div className="text-center p-3 bg-green-50 rounded-lg">
              <div className="text-2xl font-bold text-green-700">
                {Math.round(progress * 0.45)}%
              </div>
              <div className="text-xs text-green-600">{t('jobsView.quality_improved')}</div>
            </div>
            <div className="text-center p-3 bg-blue-50 rounded-lg">
              <div className="text-2xl font-bold text-blue-700">
                {Math.round((progress / 100) * (currentJob.productCount ?? 1))}
              </div>
              <div className="text-xs text-blue-600">{t('jobsView.products_enhanced')}</div>
            </div>
            <div className="text-center p-3 bg-amber-50 rounded-lg">
              <div className="text-2xl font-bold text-amber-700">
                {Math.min(Math.round(progress * 0.8), 100)}%
              </div>
              <div className="text-xs text-amber-600">{t('jobsView.seo_optimized')}</div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default JobInProgressCard;